import { type ReactNode, useCallback, useEffect } from "react";
import { authService } from "@/services/authService";
import { useAuthStore } from "@/store/authStore";
import { subscribeAuthChanges } from "@/lib/authSync";
import { useQueryClient } from "@tanstack/react-query";

export const AuthBootstrap = ({ children }: { children: ReactNode }) => {
  const setAuth = useAuthStore((state) => state.setAuth);
  const setAuthChecked = useAuthStore((state) => state.setAuthChecked);
  const logout = useAuthStore((state) => state.logout);
  const queryClient = useQueryClient();

  const refreshFromServer = useCallback(async () => {
    const tokenAtStart = useAuthStore.getState().token;
    try {
      const user = await authService.getCurrentUser({ skipAuthHeader: true });
      if (useAuthStore.getState().token === tokenAtStart) {
        setAuth(user, null);
      }
    } catch {
      if (useAuthStore.getState().token === tokenAtStart) {
        logout();
      }
    } finally {
      setAuthChecked(true);
    }
  }, [logout, setAuth, setAuthChecked]);

  useEffect(() => {
    void refreshFromServer();
  }, [refreshFromServer]);

  useEffect(() => {
    const unsubscribe = subscribeAuthChanges((message) => {
      if (message.type === "auth:logout") {
        logout();
        queryClient.clear();
        return;
      }
      void queryClient.invalidateQueries();
      void refreshFromServer();
    });
    return unsubscribe;
  }, [logout, queryClient, refreshFromServer]);

  return <>{children}</>;
};
